// Sharing a URL from a button, with a result the caller can actually report.
//
// One definition for both share controls inside League Play — the landing card's
// own icon and the app header's glyph — because they were two copies of the same
// three lines and they failed in the same silent way: the desktop path copies to
// the clipboard, and neither of them said so.
//
// The order matters:
//
//   1. navigator.share — the OS share sheet. Mobile / Android Chrome only; it
//      does not exist on a desktop browser. The sheet is its own confirmation,
//      so a caller should stay quiet on "shared". Used on EVERY platform that
//      has it — iOS gets the iOS sheet, Android the Android one.
//   2. navigator.clipboard.writeText — the modern copy. Needs a secure context
//      (https, or localhost) AND a focused document, and it REJECTS rather than
//      throwing synchronously when either is missing.
//   3. document.execCommand("copy") — the legacy copy. Deprecated but still
//      implemented everywhere, and it works in exactly the cases step 2 refuses:
//      no permission prompt, no focus requirement beyond the element it selects.
//      This is the step that keeps the button honest on the machines where the
//      Clipboard API says no.
//
// Returns "shared" | "copied" | "failed" so the caller decides the wording; this
// helper deliberately shows nothing itself.
import { event } from "./event";

// Step 0: hand the share to the Flutter shell, and STOP.
//
// Inside the QUSHi app this page is a WKWebView. When the WebView ITSELF
// presents the native share sheet, iOS adjusts its scroll view's contentInset
// and does not restore it on dismiss — the page comes back unable to reach its
// own bottom. That is the "rubber band" the client reported after sending a
// league link through Messages, and it is why every web-side repair for it was
// reverted: the same HTML behaves correctly in mobile Safari, so nothing a page
// can reach was ever going to fix it.
//
// The fix is that inside the app the sheet is presented by Flutter, over the
// WebView, so the WebView's scroll view is never touched. This side's only job
// is to hand over the URL, on the same 'Qushi' bridge the app already uses for
// Applee, Googlee and imageUpload*:
//
//     window.flutter_inappwebview.callHandler("Qushi", {
//       event: "share",
//       data: { url, title, text },
//     })
//
// SO: once the event is dispatched, this returns true and the caller returns.
// It does NOT wait for the handler, does not care what the handler resolves
// with, and above all does NOT fall back to navigator.share. An earlier revision
// did fall back — "call navigator.share if Flutter doesn't confirm" — and that
// was wrong on its own terms: that fallback IS the WebView opening its own
// sheet, which is the entire bug we are removing. There is no state in which
// this file opens a sheet inside the app.
//
// APP ONLY. Mobile Safari and every browser keep exactly the path they have
// today, because sharing there already works — the contentInset fault belongs to
// the app's WebView and Safari never had it. Two gates gate the bridge, and a
// browser fails both: `window.flutter_inappwebview` exists only because the
// Flutter shell injects it, and event() returns undefined unless
// public/index.html has set isFlutterInAppWebViewReady from the shell's own
// platform-ready event. Neither is ever true in Safari, on desktop, or in a
// mobile browser, so those fall through to navigator.share exactly as before.
export const shareViaFlutter = ({ url, title, text, message } = {}) => {
  if (!url) return false;
  // Gate 1 — the injected bridge object. Absent in every real browser.
  if (typeof window === "undefined" || !window.flutter_inappwebview) return false;
  try {
    // Gate 2 — event() is a no-op returning undefined until the shell has
    // signalled it is ready. Anything else means the event went over the bridge.
    // `message` is the finished three-block body the client specified (see
    // utils/shareMessage). The shell's native share takes ONE body, so it is
    // handed the joined string rather than having to re-assemble `text` + `url`
    // and guess the blank line. An app build that predates the field ignores it
    // and keeps using `text` + `url`, so the web side can ship first.
    const dispatched = event("share", { url, title, text, message });
    if (dispatched === undefined) return false;
    // callHandler resolves with whatever Dart returns; we neither read it nor
    // want an unhandled rejection if the handler throws.
    if (dispatched && typeof dispatched.catch === "function") dispatched.catch(() => {});
    return true;
  } catch (e) {
    // The bridge threw, so nothing was handed over and we are, in practice, not
    // in a working shell. Let the browser path have it.
    return false;
  }
};

/**
 * Is this a pointer-and-keyboard machine rather than a phone or tablet?
 *
 * Client Rev 5: "The native share desktop screen that pops up is useless. We
 * need to change how this works... On desktop, make every league share control
 * behave consistently: clicking the share icon copies that league's landing page
 * URL. Show a small confirmation toast: League link copied. Do not open the
 * Windows system share sheet."
 *
 * navigator.share EXISTS on desktop Chrome and Edge, so the sheet the client is
 * describing is Windows' own — a chooser full of Mail, Bluetooth and "Nearby
 * share" that cannot do the one thing a person sharing a league wants. Mobile is
 * untouched and deliberately so: "Share league opens the native share sheet - no
 * change; this works, so keep it as is."
 *
 * Decided on POINTER TYPE, not on user-agent sniffing. A coarse pointer means a
 * finger, which is the same population that gets a usable OS share sheet, and it
 * is the one test that gets an iPad right — iPadOS reports itself as a Mac, so a
 * UA check would send it down the desktop path and take away a sheet that works.
 * If the browser cannot answer, the UA is the fallback and the last resort is to
 * treat the machine as mobile, which only ever means "offer the sheet".
 */
const isDesktopPointer = () => {
  if (typeof window === "undefined") return false;

  const coarse = window.matchMedia?.("(pointer: coarse)");
  if (coarse && typeof coarse.matches === "boolean") return !coarse.matches;

  return !/Android|iPhone|iPad|iPod|Mobile/i.test(
    (typeof navigator !== "undefined" && navigator.userAgent) || ""
  );
};

export const shareLink = async ({ url, title, text, message }) => {
  if (!url) return "failed";

  // In the app the native sheet is Flutter's to open — see shareViaFlutter above.
  // "shared" because the sheet is its own confirmation; the caller stays quiet.
  if (shareViaFlutter({ url, title, text, message })) return "shared";

  // Client Rev 4: "The mobile Share behavior has changed. On my iPhone, I no
  // longer see the native Apple share icon or native iOS share sheet. Tapping
  // Share now uses the other share icon and only copies the link. Please restore
  // the native mobile share behavior. On iPhone, tapping Share should open the
  // native iOS share sheet with the normal sharing options, not automatically
  // copy the link. Android should use its native Android share sheet."
  //
  // Restored. The previous revision skipped the OS sheet inside the QUSHi app
  // shell, to work around the WebView losing its scroll after the app returned
  // from the share sheet (Rev 2/3). That was the wrong price to pay: it traded a
  // native, expected interaction for a silent clipboard copy on every phone, and
  // the client has now said so plainly. The sheet is back on every platform that
  // has one.
  //
  // This file used to also run a "scroll recovery" here — forced reflows and
  // listeners armed for two minutes after every share, trying to make the page
  // scrollable again afterwards. All of it is gone, because the client has since
  // confirmed the page behaves correctly in mobile Safari and only breaks inside
  // the app: the same HTML, CSS and JS, so the fault is in the WebView, not in
  // anything a page can reach. The repair could never have worked, and it was
  // charging every share on every platform a forced layout for it.
  //
  // The durable fix belongs on the Flutter side — WKWebView's scroll view keeps
  // a contentInset that iOS adjusts when a native sheet is presented over it and
  // does not restore on dismiss. Sharing here does what a share should and
  // nothing else.
  // Desktop never opens a sheet — it falls through to the clipboard steps below
  // and the caller reports "copied". See isDesktopPointer above for why this is
  // decided on pointer type.
  if (typeof navigator !== "undefined" && navigator.share && !isDesktopPointer()) {
    try {
      // WHEN THERE IS A MESSAGE, IT GOES IN `text` AND `url` IS OMITTED.
      //
      // The obvious shape — { text: "two lines", url } — is wrong on iOS, and
      // was shipped that way first. Measured on an iPhone, same league, same
      // build, sharing from Safari:
      //
      //   WhatsApp        both fields honoured  -> full message + preview  OK
      //   Apple Messages  took the URL ONLY     -> bare preview card, no text
      //
      // iOS treats `url` as THE item being shared once it is present, and a
      // target that wants a single payload gets the link and nothing else.
      // Messages is one of those. So the whole three-block message travels as
      // `text`, with the URL inside it, and nothing is passed as `url`.
      //
      // The preview is NOT lost by doing this: Messages, WhatsApp and the rest
      // linkify a URL found in the body and scrape it for the card exactly as
      // they would a bare link — which is what the Open Graph tags in
      // public/index.html answer.
      //
      // Callers that share a plain link and no message (the invite-code button)
      // pass no `message` and keep the original { title, url } shape, so the
      // sheet still offers Add to Reading List, Add Bookmark and the rest for
      // them.
      await navigator.share(
        message
          ? { title: title || document.title || "Qushi", text: message }
          : {
              title: title || document.title || "Qushi",
              ...(text ? { text } : {}),
              url,
            }
      );
      return "shared";
    } catch (e) {
      // The user closing the sheet is a decision, not a failure — say nothing.
      if (e?.name === "AbortError") return "shared";
    }
  }

  // DESKTOP FALLBACK STILL COPIES THE BARE URL, not the composed message.
  //
  // The client's message spec is about what a recipient receives from a share
  // SHEET — their own test list is iPhone Messages, Android Messages and
  // WhatsApp. A desktop copy is a different act: the link gets pasted into
  // Slack, an email, a calendar invite, where a three-line body pasted into a
  // URL field is worse than useless. The recipient still sees the full preview,
  // because that comes from the page's Open Graph tags rather than from the
  // sender's text.
  return (await copyText(url)) ? "copied" : "failed";
};

// Put a string on the clipboard, and say whether it landed.
//
// Steps 2 and 3 of the order above, on their own, because a share URL is not the
// only thing worth copying — the organiser's invite code goes through here too,
// and it must NOT offer the OS share sheet: the code is a credential to paste,
// not a page to share.
//
//   navigator.clipboard.writeText — needs a secure context (https, or localhost)
//     AND a focused document, and REJECTS rather than throwing synchronously
//     when either is missing.
//   document.execCommand("copy")  — deprecated but still implemented, and it
//     works in the cases the Clipboard API refuses. This is the step that keeps
//     a copy button honest on the machines where the modern API says no.
export const copyText = async (text) => {
  if (!text) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Fall through to the legacy path below.
  }

  try {
    const field = document.createElement("textarea");
    field.value = text;
    // Off-screen rather than hidden: execCommand needs a selectable node, and a
    // display:none one cannot be selected. `readonly` stops the mobile keyboard.
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.top = "-1000px";
    field.style.opacity = "0";
    document.body.appendChild(field);
    // iOS scrolls the page to whatever gets focus, and this element is parked a
    // thousand pixels above the top of it — so selecting it can throw the scroll
    // position away before the copy even happens. Remember where we were, and put
    // it back. Blur before removing, too: taking a focused node out of the DOM
    // leaves iOS believing something is still focused, and no focusout is ever
    // fired to correct it.
    const scroller = document.scrollingElement || document.documentElement;
    const restoreTop = scroller.scrollTop;
    const restoreLeft = scroller.scrollLeft;
    field.select();
    field.setSelectionRange(0, String(text).length);
    const ok = document.execCommand("copy");
    field.blur();
    document.body.removeChild(field);
    scroller.scrollTop = restoreTop;
    scroller.scrollLeft = restoreLeft;
    return !!ok;
  } catch (e) {
    return false;
  }
};

export default shareLink;
